// src/components/chat/RemindersPanel.jsx — Reminders & Follow-Up Schedulers
import React, { useState } from "react";
import { Plus, Clock, Trash2 } from "lucide-react";
import { useChat } from "../../context/ChatContext";
import api from "../../services/api";
import toast from "react-hot-toast";

export default function RemindersPanel() {
  const { unifiedReminders, loadUnifiedReminders } = useChat();

  const [filter, setFilter] = useState("pending"); // 'pending' | 'completed' | 'all'
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDueAt, setNewDueAt] = useState("");
  const [newNote, setNewNote] = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDueAt) return;

    try {
      await api.post("/reminders/unified", {
        title: newTitle.trim(),
        dueAt: new Date(newDueAt).toISOString(),
        note: newNote.trim() || null
      });
      toast.success("Reminder created!");
      setNewTitle("");
      setNewDueAt("");
      setNewNote("");
      setShowAddForm(false);
      loadUnifiedReminders();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create reminder");
    }
  };

  const handleToggleComplete = async (id, currentStatus) => {
    try {
      const nextStatus = currentStatus === "done" ? "pending" : "done";
      await api.patch(`/reminders/unified/${id}`, { status: nextStatus });
      loadUnifiedReminders();
    } catch (_) {}
  };

  const handleSnooze = async (id, minutes) => {
    try {
      await api.patch(`/reminders/unified/${id}`, { snoozeMinutes: minutes });
      toast.success(`Snoozed for ${minutes} minutes`);
      loadUnifiedReminders();
    } catch (_) {}
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reminders/unified/${id}`);
      toast.success("Reminder deleted");
      loadUnifiedReminders();
    } catch (_) {}
  };

  const formatDueDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const filtered = unifiedReminders.filter((r) => {
    if (filter === "pending") return r.status === "pending" || r.status === "sent" || r.status === "snoozed";
    if (filter === "completed") return r.status === "done";
    return true;
  });

  return (
    <div className="reminders-panel">
      {/* Header / Subnav */}
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--surface-2)"
        }}
      >
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            onClick={() => setFilter("pending")}
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 600,
              border: "none",
              background: filter === "pending" ? "var(--primary)" : "transparent",
              color: filter === "pending" ? "#fff" : "var(--muted)",
              cursor: "pointer"
            }}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter("completed")}
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 600,
              border: "none",
              background: filter === "completed" ? "var(--primary)" : "transparent",
              color: filter === "completed" ? "#fff" : "var(--muted)",
              cursor: "pointer"
            }}
          >
            Completed
          </button>
        </div>

        <button
          onClick={() => setShowAddForm(prev => !prev)}
          className="chat-icon-btn"
          style={{ width: "30px", height: "30px" }}
          title="Add Reminder"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <form
          onSubmit={handleCreate}
          style={{
            padding: "12px 14px",
            borderBottom: "1px solid var(--border)",
            background: "rgba(255, 255, 255, 0.02)",
            display: "flex",
            flexDirection: "column",
            gap: "8px"
          }}
        >
          <input
            type="text"
            placeholder="Reminder title..."
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              fontSize: "13px"
            }}
            autoFocus
          />
          <input
            type="datetime-local"
            value={newDueAt}
            onChange={e => setNewDueAt(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              fontSize: "13px"
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              style={{ padding: "4px 10px", borderRadius: "6px", background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", fontSize: "12px", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newTitle.trim() || !newDueAt}
              style={{ padding: "4px 12px", borderRadius: "6px", background: "var(--primary)", border: "none", color: "#fff", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
            >
              Add Reminder
            </button>
          </div>
        </form>
      )}

      {/* Reminders List */}
      <div className="reminders-list">
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--muted)", margin: "auto", padding: "20px", fontSize: "12.5px" }}>
            No {filter} reminders. Click <strong>+</strong> to add one or ask Buddy!
          </div>
        ) : (
          filtered.map((rem) => {
            const isDone = rem.status === "done";

            return (
              <div key={rem.id} className={`reminder-card ${isDone ? "completed" : ""}`}>
                <input
                  type="checkbox"
                  checked={isDone}
                  onChange={() => handleToggleComplete(rem.id, rem.status)}
                  style={{ width: "16px", height: "16px", cursor: "pointer", marginTop: "2px", accentColor: "var(--primary)" }}
                />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="reminder-title" style={{ textDecoration: isDone ? "line-through" : "none" }}>
                    {rem.title}
                  </div>
                  <div className="reminder-meta">
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <Clock size={11} /> {formatDueDate(rem.due_at_utc)}
                    </span>
                    {rem.leadName && (
                      <span style={{ color: "var(--primary)" }}>Lead: {rem.leadName}</span>
                    )}
                  </div>
                </div>

                {!isDone && (
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <button
                      onClick={() => handleSnooze(rem.id, 15)}
                      style={{ padding: "2px 6px", fontSize: "10.5px", background: "var(--surface-3)", border: "1px solid var(--border)", borderRadius: "4px", color: "var(--muted)", cursor: "pointer" }}
                      title="Snooze 15 minutes"
                    >
                      +15m
                    </button>
                    <button
                      onClick={() => handleSnooze(rem.id, 60)}
                      style={{ padding: "2px 6px", fontSize: "10.5px", background: "var(--surface-3)", border: "1px solid var(--border)", borderRadius: "4px", color: "var(--muted)", cursor: "pointer" }}
                      title="Snooze 1 hour"
                    >
                      +1h
                    </button>
                    <button
                      onClick={() => handleDelete(rem.id)}
                      style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: "2px" }}
                      title="Delete"
                    >
                      <Trash2 size={13} style={{ color: "var(--danger)" }} />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
